import {Component, computed, inject, linkedSignal, signal} from "@angular/core";
import {ListComponent} from "../../../../../../../shared/shared-components/list/list.component";
import {MatDivider} from "@angular/material/divider";
import {SharedModule} from "../../../../../../../shared/shared.module";
import {FetchDataType, Gender, SimGroup, SimUser} from "@models";
import {Translations} from "@translations";
import {ListItemComponent} from "@common/ui/list-item/list-item.component";
import {CreatePeerPracticeSessionDialogStateService} from "../../service/create-peer-practice-session-dialog-state.service";
import {derivedAsync} from "ngxtension/derived-async";
import {EnumLabel, EnumLabelPipe} from "../../../../../../../shared/shared-pipes/enum-label.pipe";
import {explicitEffect} from "ngxtension/explicit-effect";
import {UserDetailsComponent} from "@common/components/user-details/user-details.component";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";

@Component({
	selector: "sim-peer-practice-session-step2",
	standalone: true,
	imports: [ListComponent, MatDivider, SharedModule, ListItemComponent, UserDetailsComponent],
	animations: [zoomInOnEnterAnimation({duration: 150}), zoomOutOnLeaveAnimation({duration: 150})],
	templateUrl: "./peer-practice-session-step2.component.html",
	styleUrl: "./peer-practice-session-step2.component.scss",
})
export class PeerPracticeSessionStep2Component {
	// Template constants
	protected readonly Gender = Gender;
	protected readonly EnumLabelPipe = EnumLabelPipe;
	protected readonly EnumLabel = EnumLabel;
	protected readonly FetchDataType = FetchDataType;
	protected readonly T = Translations;

	// Properties
	protected readonly selectedGroup = signal<SimGroup | undefined>(undefined);
	protected readonly groupMembers = derivedAsync<SimUser[]>(() => this.computeGroupMembers(this.selectedGroup()?.id), {initialValue: []});
	protected readonly selectedMember = signal<SimUser | undefined>(undefined);
	protected readonly excludeIds = computed(() => {
		return this.#stateService.session()?.userSubGroupIds ?? [];
	});
	protected readonly viewGroups = linkedSignal<SimGroup[]>(() => {
		return this.#stateService.session()?.userSubGroups ?? [];
	});

	// Injections
	readonly #stateService = inject(CreatePeerPracticeSessionDialogStateService);

	protected async removeUserGroup(group: SimGroup): Promise<void> {
		this.viewGroups.update((value) => value.filter((g) => g.id !== group.id));
		await this.#stateService.removeUserGroupById(group.id);

		// Unselect removed group
		if (this.selectedGroup()?.id === group.id) {
			this.selectedGroup.set(undefined);
			this.selectedMember.set(undefined);
		}
	}

	protected addUserGroup(group: SimGroup): void {
		this.viewGroups.update((value) => [...value, group]);
		this.#stateService.addUserGroupById(group.id);
	}

	private async computeGroupMembers(groupId: string | undefined): Promise<SimUser[]> {
		if (!groupId) {
			return [];
		}
		return await this.#stateService.getGroupMembersByGroupId(groupId);
	}

	readonly #autoSelectGroup = explicitEffect([this.viewGroups], () => {
		const viewGroups = this.viewGroups();
		const selectedGroup = this.selectedGroup();

		if (viewGroups.length && !selectedGroup) {
			this.selectedGroup.set(viewGroups.at(-1));
		} else if (!viewGroups.length) {
			this.selectedGroup.set(undefined);
		}
	});

	readonly #autoSelectMember = explicitEffect([this.groupMembers, this.selectedGroup], () => {
		const groupMembers = this.groupMembers();

		if (groupMembers.length) {
			this.selectedMember.set(groupMembers.at(0));
		} else if (!groupMembers.length) {
			this.selectedMember.set(undefined);
		}
	});
}
